import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Statistic, message } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { Helmet } from 'react-helmet-async';
import TempleStatsByProvince from './TempleStatsByProvince';
import BarChartComponent from './BarChartComponent';
import ProvinceComparisonChart from "./ProvinceComparisonChart";


// ฟังก์ชันแปลง YYYY-MM เป็นชื่อเดือนภาษาไทยแบบย่อ
const formatMonthThai = (monthStr) => {
  const months = [
    'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
    'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
  ];
  const [year, month] = monthStr.split('-').map(Number);
  return `${months[month - 1]} ${year + 543}`;
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    temples: { current: 0, lastMonth: 0, total: 0 },
    monks: { current: 0, lastMonth: 0, total: 0 },
    users: { current: 0, lastMonth: 0, total: 0 },
  });


  const [profile, setProfile] = useState(null);
  const [temples, setTemples] = useState([]);
  const [provinceStat, setProvinceStat] = useState([]);
  const [loading, setLoading] = useState(false);


  const [chartData, setChartData] = useState([]);

  useEffect(() => {

    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/api/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        //console.log('res', res.data);

        // แปลงเดือน
        const formattedChart = res.data.chart.map(item => ({
          ...item,
          month: formatMonthThai(item.month),
        }));

        setStats(res.data.summary);
        setChartData(formattedChart);
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    };

    fetchProfile();
    fetchTemples();
    fetchStats();


  }, []);


  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = { ...res.data, address: { province: res.data.province, district: res.data.district, subdistrict: res.data.subdistrict } };
      //console.log('Me:', data);
      setProfile(data);

    } catch (err) {
      message.error('ไม่สามารถโหลดข้อมูลโปรไฟล์');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemples = async () => {

    setLoading(true);

    // ดึง token และ user  จาก localStorage
    const token = localStorage.getItem('token');

    try {

      // ถ้ามี token ก็แนบไปใน header
      const res = await axios.get(`${API_BASE_URL}/api/get-temples`, {
        headers: {
          Authorization: `Bearer ${token}`, // ส่ง token ไปใน header
        },
      });

      const userDataRes = await axios.get(`${API_BASE_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const profileData = { ...userDataRes.data, address: { province: userDataRes.data.province, district: userDataRes.data.district, subdistrict: userDataRes.data.subdistrict } };
      //console.log('temples', res.data.temples);
      //console.log('profile:', data);
      //const province = profileData.province;
      setTemples(res.data.temples);
      const stats = generateTempleStatsByProvince(res.data.temples);
      //console.log(stats);
      setProvinceStat(stats);


    } catch (error) {
      message.error('เกิดข้อผิดพลาดขณะดึงรายชื่อวัด !!!');
    } finally {
      setLoading(false);
    }
  };

  function generateTempleStatsByProvince(data) {
    // จังหวัดที่ต้องการ
    const provinces = [
      'เพชรบูรณ์', 'นครสวรรค์', 'พิจิตร', 'พิษณุโลก',
      'อุตรดิตถ์', 'สุโขทัย', 'ตาก', 'กำแพงเพชร'
    ];

    // ประเภทวัดที่ต้องการนับ
    const typeMapping = {
      temples: ['พัทธสีมา', 'วิสุงคามสีมา', 'วัดราษฎร์', 'พระอารามหลวง'],
      monastery: ['ที่พักสงฆ์'],
      pattasima: ['พัทธสีมา'],
      wisung: ['วิสุงคามสีมา'],
      raj: ['วัดราษฎร์'],
      royal: ['พระอารามหลวง'],
    };

    const result = provinces.map(province => {
      const provinceData = data.filter(item => item.province === province);

      const stats = {
        province,
        totalAll: provinceData.length,
        temples: 0,
        monastery: 0,
        pattasima: 0,
        wisung: 0,
        raj: 0,
        royal: 0,
      };

      for (const item of provinceData) {
        const type = item.type;

        if (typeMapping.temples.includes(type)) stats.temples++;
        if (typeMapping.monastery.includes(type)) stats.monastery++;
        if (typeMapping.pattasima.includes(type)) stats.pattasima++;
        if (typeMapping.wisung.includes(type)) stats.wisung++;
        if (typeMapping.raj.includes(type)) stats.raj++;
        if (typeMapping.royal.includes(type)) stats.royal++;
      }

      return stats;
    });

    return result;
  }


  //นับประเภทวัด
  function countAllTypes(items) {
    return {
      totalAll: items.length,
      temples: items.filter(i =>
        ['พัทธสีมา', 'วิสุงคามสีมา', 'วัดราษฎร์', 'พระอารามหลวง'].includes(i.type)
      ).length,
      monastery: items.filter(i => i.type === 'ที่พักสงฆ์').length,
      pattasima: items.filter(i => i.type === 'พัทธสีมา').length,
      wisung: items.filter(i => i.type === 'วิสุงคามสีมา').length,
      raj: items.filter(i => i.type === 'วัดราษฎร์').length,
      royal: items.filter(i => i.type === 'อารามหลวง').length,
    };
  }

  //กรองข้อมูลทั้งหมดแยกจังหวัด
  const filterByProvince = async (dataArray, provinceName) => {
    return dataArray.filter(item => item.province === provinceName);
  }

  const getChange = (current, last) => {
    const diff = current - last;
    const percent = last === 0 ? 100 : Math.round((diff / last) * 100);
    return { diff, percent };
  };

  const cards = [
    { title: 'จำนวนวัดในระบบ', key: 'temples' },
    { title: 'จำนวนพระภิกษุ/สามเณรในระบบ', key: 'monks' },
    // { title: 'จำนวนผู้ใช้', key: 'users' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Helmet>
        <title>ระบบสารสนเทศคณะสงฆ์ สำนักงานเจ้าคณะภาค 4-5 (ธรรมยุต)</title>
        <meta name="description" content="ระบบสารสนเทศคณะสงฆ์ ภาค 4-5 (ธรรมยุต)" />
        <meta property="og:title" content={`สำนักงานเจ้าคณะภาค 4-5 (ธรรมยุต)`} />
        <meta property="og:description" content="ระบบสารสนเทศคณะสงฆ์ ภาค 4-5 (ธรรมยุต)" />
      </Helmet>
      <Row gutter={16}>
        {cards.map(({ title, key }) => {
          const { current, lastMonth, total } = stats[key];
          const { percent } = getChange(current, lastMonth);
          const isUp = percent >= 0;
          return (
            <Col xs={24} sm={8} key={key}>
              <Card>
                <Statistic
                  title={title}
                  value={total}
                  precision={0}
                  valueStyle={{ color: isUp ? '#3f8600' : '#cf1322' }}
                  prefix={isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                  suffix={` (${isUp ? '+' : '-'}${Math.abs(percent)}%)`}
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      <Card title="ภาพรวม" style={{ marginTop: 24, marginBottom: 100 }}>

        {provinceStat && (<TempleStatsByProvince data={provinceStat} />)}


        {provinceStat && (<BarChartComponent stats={provinceStat} />)}


        {provinceStat && (
          <>
            <div style={{ marginTop: 50 }}>
              <h2>กราฟเปรียบเทียบข้อมูลแต่ละจังหวัด ภาค ๔-๕ (ธ)</h2>
              <ProvinceComparisonChart data={provinceStat} />
            </div>
          </>
        )}

        <div style={{ marginTop: 50 }}>
          <h2>แนวโน้มการเปลี่ยนแปลง</h2>
          {chartData && (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="temples" stroke="#1890ff" name="วัด" />
                  <Line type="monotone" dataKey="monks" stroke="#52c41a" name="พระ" />
                  <Line type="monotone" dataKey="users" stroke="#faad14" name="ผู้ใช้" />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;

